import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

/**
 * StyleTrimConsumption — the first writer style_trim_consumption has ever had, driven by
 * what the STYLE needs rather than by what the trims master happens to hold.
 *
 * WHY A WORKLIST AND NOT A FORM. The first cut opened on a trim dropdown built from
 * trims_master. For UBS1SP25TS734 that offered 2 selectable trims out of 24, neither of
 * them among the 12 accessories the style actually carries — a blank form, a dead end, and
 * no way to learn from the screen why. With 34 styles to key by hand that is 34 dead ends.
 *
 * The GPO already knows: its `accessories` list is what the estimate priced, and it is the
 * same list deriveTrim walks at generation time. So every accessory gets a row, in one of
 * three states, and the merchant can see the whole job — including the parts of it that are
 * blocked and who has to unblock them.
 *
 *   ready         the trim is configured; type the per-garment figure
 *   unconfigured  the trim exists but has no base unit / pack size — TRIMS MASTER first,
 *                 because recording against it would make every ARS for this style refuse
 *   no_master     no trim with that spelling exists at all — the identity gap. Not
 *                 workaroundable from here, and deliberately not creatable from here:
 *                 a master created by whoever is keying consumption is how you get 35
 *                 spellings of one item, which is the problem being fixed.
 */
export default function StyleTrimConsumption() {
  const nav = useNavigate();
  const [styles, setStyles] = useState([]);
  const [style, setStyle] = useState('');
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});      // accessory -> typed value, before save
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/oe/style-trim-consumption/styles').then(r => setStyles(r.data.data || [])).catch(() => setStyles([]));
  }, []);

  const load = useCallback(() => {
    if (!style) { setData(null); return; }
    api.get('/oe/style-trim-consumption/worklist?style_no=' + encodeURIComponent(style))
      .then(r => { setData(r.data.data); setDraft({}); })
      .catch(() => setData(null));
  }, [style]);
  useEffect(() => { load(); }, [load]);

  const save = async (row) => {
    const v = Number(draft[row.accessory]);
    if (!Number.isFinite(v) || v <= 0) { setErr('Enter a positive consumption before saving.'); return; }
    setErr(''); setBusy(row.accessory);
    try {
      if (row.consumption_id) {
        await api.patch('/oe/style-trim-consumption/' + row.consumption_id, { consumption_per_garment: v });
      } else {
        await api.post('/oe/style-trim-consumption', { style_no: style, trim_id: row.trim_id, consumption_per_garment: v });
      }
      load();
    } catch (e) { setErr(e.response?.data?.error || 'Failed to save'); }
    finally { setBusy(''); }
  };

  const clear = async (row) => {
    setErr(''); setBusy(row.accessory);
    try { await api.delete('/oe/style-trim-consumption/' + row.consumption_id); load(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed to remove'); }
    finally { setBusy(''); }
  };

  const inputS = { width: '100%', padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
  const labelS = { fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 4, textTransform: 'uppercase' };
  const th = { padding: '8px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid #f5f5f5', verticalAlign: 'middle' };

  const chip = (bg, fg, text, title) => (
    <span title={title} style={{ fontSize: 10, fontWeight: 700, background: bg, color: fg, padding: '2px 7px', borderRadius: 6, whiteSpace: 'nowrap' }}>{text}</span>
  );

  const p = data?.progress;

  return (
    <div style={{ padding: 20 }}>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 14, maxWidth: 820 }}>
        How much of each trim one garment of this style uses, stated in the unit the trim is <strong>measured</strong> in —
        metres of thread, pieces of button. The ARS converts that into an order using the trim's pack size, so nobody divides
        by a box size by hand. The list below is the style's own accessories, taken from its GPO.
      </div>

      <div style={{ maxWidth: 420, marginBottom: 16 }}>
        <label style={labelS}>Style</label>
        <select style={inputS} value={style} onChange={e => setStyle(e.target.value)}>
          <option value="">Select a style…</option>
          {styles.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '9px 12px', borderRadius: 6, marginBottom: 12, fontSize: 12.5, maxWidth: 820 }}>{err}</div>}

      {style && data && (
        <>
          {/* Progress reads against the WHOLE job, blocked lines included. A figure that
              counted only what is currently possible would say "3 of 3" on a style where
              nine lines cannot be touched, which is the opposite of informative. */}
          <div style={{ display: 'flex', gap: 18, alignItems: 'baseline', padding: '10px 14px', background: '#F7F6F3', borderRadius: 8, marginBottom: 14, maxWidth: 820, fontSize: 12.5 }}>
            <span style={{ fontWeight: 700, color: '#2D2C72' }}>{p.recorded} of {p.total} recorded</span>
            {p.ready > 0 && <span style={{ color: '#166534' }}>{p.ready} ready to type</span>}
            {p.unconfigured > 0 && <span style={{ color: '#9A6B00' }}>{p.unconfigured} need the trim configured</span>}
            {p.no_master > 0 && <span style={{ color: '#A32D2D' }}>{p.no_master} have no trim master row</span>}
          </div>

          <div style={{ overflowX: 'auto', border: '1px solid #E8E7E7', borderRadius: 8, maxWidth: 1020 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: '#F7F6F3' }}>
                {['Accessory', 'State', 'Per garment', 'Ordered in', 'Pack', ''].map(h => <th key={h} style={th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {data.rows.length === 0 && (
                  <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: '#aaa', padding: 22 }}>
                    This style's GPO lists no accessories, so there is nothing to record against it.
                  </td></tr>
                )}
                {data.rows.map(r => {
                  const blocked = r.state === 'unconfigured' || r.state === 'no_master';
                  return (
                    <tr key={r.accessory} style={{ background: blocked ? '#FEFCF8' : 'transparent' }}>
                      <td style={{ ...td, fontWeight: 600 }}>
                        {r.accessory}
                        {!r.from_estimate && <span style={{ marginLeft: 6, fontSize: 10, color: '#888' }}>(not on the estimate)</span>}
                      </td>
                      <td style={td}>
                        {r.state === 'recorded' && chip('#DCFCE7', '#166534', '✓ recorded')}
                        {r.state === 'ready' && chip('#EEEDF8', '#3B3A8F', 'ready')}
                        {r.state === 'unconfigured' && chip('#FBF7EF', '#9A6B00', 'trim not configured', 'The trim exists but has no base unit / pack size')}
                        {r.state === 'no_master' && chip('#FCEBEB', '#A32D2D', 'no trim master row', 'No trim with this exact spelling exists')}
                      </td>
                      <td style={{ ...td, minWidth: 150 }}>
                        {blocked ? <span style={{ color: '#bbb' }}>—</span> : (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <input style={{ ...inputS, width: 96 }} type="number" step="0.0001" min="0"
                                   value={draft[r.accessory] ?? (r.consumption_per_garment ?? '')}
                                   onChange={e => setDraft(d => ({ ...d, [r.accessory]: e.target.value }))} />
                            <span style={{ fontSize: 11, color: '#888' }}>{r.uom_base}</span>
                          </div>
                        )}
                      </td>
                      <td style={td}>{r.order_unit || '—'}</td>
                      <td style={{ ...td, color: '#666' }}>
                        {r.pack_size != null ? `${Number(r.pack_size)} ${r.uom_base} / ${r.order_unit}${r.ply ? ` · ${r.ply}-ply` : ''}` : '—'}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        {r.state === 'unconfigured' && (
                          <button onClick={() => nav('/masters')} style={{ border: 'none', background: '#FBF7EF', color: '#9A6B00', padding: '4px 10px', borderRadius: 5, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                            Set base unit &amp; pack →
                          </button>
                        )}
                        {r.state === 'no_master' && (
                          <span style={{ fontSize: 11, color: '#A32D2D' }}>needs a trims master row</span>
                        )}
                        {!blocked && (
                          <>
                            <button onClick={() => save(r)} disabled={busy === r.accessory}
                              style={{ border: 'none', background: '#3B3A8F', color: '#fff', padding: '4px 12px', borderRadius: 5, fontSize: 11, cursor: 'pointer', fontWeight: 600, marginRight: 6 }}>
                              {busy === r.accessory ? '…' : r.consumption_id ? 'Update' : 'Save'}
                            </button>
                            {r.consumption_id && (
                              <button onClick={() => clear(r)} disabled={busy === r.accessory}
                                style={{ border: 'none', background: 'none', color: '#B91C1C', cursor: 'pointer', fontSize: 13 }}>✕</button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {(p.unconfigured > 0 || p.no_master > 0) && (
            <div style={{ marginTop: 14, padding: '11px 13px', background: '#FBF7EF', border: '1px solid #f6d195', borderRadius: 6, fontSize: 11.5, color: '#9A6B00', maxWidth: 1020, lineHeight: 1.6 }}>
              <strong>{p.unconfigured + p.no_master} of {p.total} lines cannot be recorded yet.</strong>
              {p.unconfigured > 0 && <> {p.unconfigured} {p.unconfigured === 1 ? 'trim needs' : 'trims need'} a base unit and pack size on the Trims &amp; Accessories Master —
                recording a consumption against one of those would make <em>every</em> ARS for this style refuse to generate, so it is blocked here rather than discovered later.</>}
              {p.no_master > 0 && <> {p.no_master} {p.no_master === 1 ? 'accessory has' : 'accessories have'} no trims master row with that exact spelling.
                That is not fixable from this screen and is not meant to be — a master created while keying consumption is how one item ends up with a dozen spellings.</>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
